import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import { getDepartmentConfig } from "../../../Departments/utils/departmentConfig";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import { AdviserChargesProvider, useAdviserCharges } from "../context/AdviserChargesContext";

import ChargesSummary from "../tabs/ChargesSummary";
import IOSummary from "../tabs/IOSummary";
import QuilterSummary from "../tabs/QuilterSummary";
import SuspenseSummary from "../tabs/SuspenseSummary";
import FinplanSummary from "../tabs/FinplanSummary";
import CommissionStatements from "../tabs/CommissionStatements";
import OpeningBalances from "../tabs/OpeningBalances";
import ClosingBalances from "../tabs/ClosingBalances";
import Tabs from "../../../DesignSystem/Tabs/Tabs";
import { MONTHLY_PAYMENT_TABS } from "../constants/monthlyPaymentTabs";
import { formatMonthKey } from "../utils/chargeDates";
import { downloadMonthlyPaymentsWorkbook } from "../utils/exportMonthlyPaymentsWorkbook";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import { useUserEmail } from "../../../SignIn/UserEmailContext";
import { useAdviserList } from "../../../../context/Adviser/Utils/AdviserEmailList";
import { useAdviserListWithUnappointed } from "../../../../context/Adviser/Utils/UnappointedAppointedAdviserEmailList";
import "../../../../css/Finance/MonthlyPayments.css";

export const getSummaryMonths = (today = new Date()) =>
  [2, 1, 0].map((offset) => {
    const date = new Date(today.getFullYear(), today.getMonth() - offset, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    return {
      value,
      label: formatMonthKey(value),
    };
  });

const TAB_COMPONENTS = {
  opening: OpeningBalances,
  closing: ClosingBalances,
  charges: ChargesSummary,
  io: IOSummary,
  quilter: QuilterSummary,
  suspense: SuspenseSummary,
  finplan: FinplanSummary,
  commission: CommissionStatements,
};

const MonthlyPayments = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tab: requestedTab } = useParams();
  const matchedTab = MONTHLY_PAYMENT_TABS.find(
    (tab) => tab.path === requestedTab,
  );
  const activeTab = matchedTab?.id || "charges";
  const ActiveTab = TAB_COMPONENTS[activeTab];
  const {
    monthOptions,
    selectedMonth,
    setSelectedMonth,
    loadedChargeRecords,
    chargeRecords,
    chargesLoading,
    chargeErrors,
    incomePeriods,
  } = useAdviserCharges();
  const requestedMonth = searchParams.get("month");
  const [interactionLoading, setInteractionLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [closingBalanceExport, setClosingBalanceExport] = useState({
    month: "",
    rows: [],
    ready: false,
  });
  const { userEmail } = useUserEmail();
  const { advisers } = useAdviserList();
  const {
    advisers: advisersWithUnappointed,
    loadingAdvisers: loadingAdvisersWithUnappointed,
  } = useAdviserListWithUnappointed();
  const exportAdvisers = useMemo(() => {
    const byEmail = new Map();
    [...advisers, ...advisersWithUnappointed].forEach((adviser) => {
      const email = String(adviser.email || adviser.id || "").trim().toLowerCase();
      if (!email) return;
      byEmail.set(email, { ...byEmail.get(email), ...adviser, email });
    });
    return [...byEmail.values()];
  }, [advisers, advisersWithUnappointed]);
  const interactionTimer = useRef(null);
  const updateClosingBalanceExport = useCallback((next) => {
    setClosingBalanceExport(next);
  }, []);
  useEffect(() => () => window.clearTimeout(interactionTimer.current), []);
  const showInteractionLoading = (event) => {
    const input = event.target;
    if (input?.type !== "radio" && input?.type !== "search") return;
    setInteractionLoading(true);
    window.clearTimeout(interactionTimer.current);
    interactionTimer.current = window.setTimeout(
      () => setInteractionLoading(false),
      350,
    );
  };
  useEffect(() => {
    if (monthOptions.some((option) => option.value === requestedMonth)) {
      setSelectedMonth(requestedMonth);
    }
  }, [incomePeriods, monthOptions, requestedMonth, setSelectedMonth]);
  const setActiveTab = (id) =>
    navigate(
      `${APP_ROUTES.MONTHLY_PAYMENTS}/${MONTHLY_PAYMENT_TABS.find((tab) => tab.id === id).path}?month=${selectedMonth}`,
    );
  const { userPermissions } = useUserPermissions();
  const { Finance } = getDepartmentConfig(
    Array.isArray(userPermissions) ? userPermissions : [],
  );
  const canAccess =
    Finance.show &&
    Finance.cards.some(
      (card) => card.route === APP_ROUTES.MONTHLY_PAYMENTS,
    );
  const downloadExcel = async () => {
    setDownloadingExcel(true);
    setDownloadError("");
    try {
      await downloadMonthlyPaymentsWorkbook({
        selectedMonth,
        chargeRecords,
        balanceChargeRecords: loadedChargeRecords,
        userEmail,
        incomePeriods,
        advisers: exportAdvisers,
        balanceAdvisers: advisers,
        closingBalanceRowsOverride:
          closingBalanceExport.ready &&
          closingBalanceExport.month === selectedMonth
            ? closingBalanceExport.rows
            : null,
      });
    } catch (error) {
      setDownloadError(error.message || "Unable to create the Monthly Payments workbook.");
    } finally {
      setDownloadingExcel(false);
    }
  };

  if (!canAccess) {
    return <Navigate to={APP_ROUTES.DEPARTMENT_ALL_STAFF} replace />;
  }
  if (!matchedTab) {
    return <Navigate to={APP_ROUTES.CHARGES_SUMMARY_DASHBAORD} replace />;
  }

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Monthly Payments</title>
      </Helmet>
      <Sidebar isSetExpanded={true} />

      <main
        className="Main-Container monthly-payments-page column gap-m"
        onChangeCapture={showInteractionLoading}
      >
        {interactionLoading ? (
          <div className="monthly-payments-page-loading" role="status" aria-live="polite">
            <span className="monthly-payments-spinner" aria-hidden="true" />
            <span className="FP_Content_Semibold">Updating...</span>
          </div>
        ) : null}
        <Breadcrumb
          items={[
            { label: "Finance", to: "/Department/Finance" },
            { label: "Monthly Payments" },
          ]}
        />
        <div className="row space-between align-center gap-l">
          <h3 className="FP_H3">Monthly Payments</h3>
          <PrimaryButton
            type="button"
            label={downloadingExcel ? "DOWNLOADING EXCEL..." : "DOWNLOAD EXCEL"}
            onClick={downloadExcel}
            disabled={
              downloadingExcel ||
              chargesLoading ||
              loadingAdvisersWithUnappointed ||
              (activeTab === "closing" &&
                (!closingBalanceExport.ready ||
                  closingBalanceExport.month !== selectedMonth))
            }
          />
        </div>
        {downloadError ? (
          <p className="FP_Help_Text text-error" role="alert">{downloadError}</p>
        ) : null}
        <Tabs
          tabs={MONTHLY_PAYMENT_TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        <ActiveTab
          selectedMonth={selectedMonth}
          monthOptions={monthOptions}
          onMonthChange={setSelectedMonth}
          chargeRecords={chargeRecords}
          chargesLoading={chargesLoading}
          chargeErrors={chargeErrors}
          onBalanceRowsChange={updateClosingBalanceExport}
        />
      </main>
    </div>
  );
};

const MonthlyPaymentsWithCharges = () => {
  const { userPermissions } = useUserPermissions();
  const { Finance } = getDepartmentConfig(Array.isArray(userPermissions) ? userPermissions : []);
  if (!Finance.show || !Finance.cards.some((card) => card.route === APP_ROUTES.MONTHLY_PAYMENTS)) {
    return <Navigate to={APP_ROUTES.DEPARTMENT_ALL_STAFF} replace />;
  }
  return <AdviserChargesProvider><MonthlyPayments /></AdviserChargesProvider>;
};
export default MonthlyPaymentsWithCharges;
